import { useEffect, useMemo, useState } from 'react';
import api from '../api/axios';

const TYPES = [
  'Job seeker support',
  'Employer registration',
  'Pricing and packages',
  'Corporate recruitment',
  'Technical support',
  'Partnership enquiries',
];

const STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'resolved', label: 'Resolved' },
];
const STATUS_RANK = { new: 0, contacted: 1, resolved: 2 };

const STATUS_STYLES = {
  new: 'bg-amber-50 text-amber-600',
  contacted: 'bg-blue-50 text-blue-600',
  resolved: 'bg-[#ECFDF5] text-[#10B981]',
};

const TYPE_TONES = {
  'Job seeker support': 'bg-purple-50 text-purple-600 border-purple-100',
  'Employer registration': 'bg-indigo-50 text-indigo-600 border-indigo-100',
  'Pricing and packages': 'bg-emerald-50 text-emerald-600 border-emerald-100',
  'Corporate recruitment': 'bg-blue-50 text-blue-600 border-blue-100',
  'Technical support': 'bg-amber-50 text-amber-600 border-amber-100',
  'Partnership enquiries': 'bg-pink-50 text-pink-600 border-pink-100',
};
const typeTone = (t) => TYPE_TONES[t] || 'bg-gray-100 text-gray-600 border-gray-200';

const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : '—';

const waLink = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return null;
  return `https://wa.me/${digits.length === 10 ? '91' + digits : digits}`;
};

const FIELD = 'w-full border border-gray-200 rounded-full px-4 py-2 text-sm bg-white text-gray-700 hover:border-purple-300 transition focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';
const SELECT = `${FIELD} appearance-none cursor-pointer pr-9`;
const CAPTION = 'block text-xs font-semibold text-gray-500 mb-1 ml-1';
const ACT = 'inline-flex items-center justify-center gap-1 h-8 px-3 rounded-full text-xs font-semibold border shadow-sm transition whitespace-nowrap disabled:opacity-40';

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const presetRange = (days) => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - (days - 1));
  return { from: iso(from), to: iso(to) };
};
const DATE_PRESETS = [
  { label: 'Today', days: 1 },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
];

const compareSort = (av, bv, dir) => {
  const aEmpty = av === null || av === undefined || av === '';
  const bEmpty = bv === null || bv === undefined || bv === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const r =
    typeof av === 'number' && typeof bv === 'number'
      ? av - bv
      : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
  return dir === 'asc' ? r : -r;
};

function SortTh({ label, sortKey, sort, onSort, className = '' }) {
  const active = sort.key === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      title="Click to sort"
      className={`text-left px-4 py-3 font-semibold cursor-pointer select-none whitespace-nowrap transition hover:text-[#DB2777] ${active ? 'text-[#DB2777]' : ''} ${className}`}
    >
      {label}
      <span className={`ml-1 ${active ? '' : 'opacity-40'}`}>{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
    </th>
  );
}

function SelectBox({ caption, value, onChange, children }) {
  return (
    <div>
      <label className={CAPTION}>{caption}</label>
      <div className="relative">
        <select value={value} onChange={onChange} className={SELECT}>{children}</select>
        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-purple-500 text-[10px]">▼</span>
      </div>
    </div>
  );
}

function Avatar({ name }) {
  return (
    <div className="w-9 h-9 shrink-0 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#DB2777] text-white flex items-center justify-center text-sm font-bold">
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
}

function Pager({ page, lastPage, onPage }) {
  const nums = [];
  for (let p = Math.max(1, page - 2); p <= Math.min(lastPage, page + 2); p++) nums.push(p);
  const btn = 'px-3 py-1.5 rounded-lg text-xs font-semibold border transition disabled:opacity-40 disabled:cursor-not-allowed';
  return (
    <div className="flex items-center gap-1">
      <button className={`${btn} bg-white hover:bg-purple-50`} disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className={`${btn} bg-white hover:bg-purple-50`} disabled={page <= 1} onClick={() => onPage(page - 1)}>Prev</button>
      {nums.map((p) => (
        <button
          key={p}
          onClick={() => onPage(p)}
          className={`${btn} ${p === page ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm' : 'bg-white hover:bg-purple-50'}`}
        >
          {p}
        </button>
      ))}
      <button className={`${btn} bg-white hover:bg-purple-50`} disabled={page >= lastPage} onClick={() => onPage(page + 1)}>Next</button>
      <button className={`${btn} bg-white hover:bg-purple-50`} disabled={page >= lastPage} onClick={() => onPage(lastPage)}>»</button>
    </div>
  );
}

function EmptyState({ icon, title, hint }) {
  return (
    <div className="py-14 text-center">
      <div className="mx-auto w-14 h-14 rounded-full bg-purple-50 flex items-center justify-center text-2xl">{icon}</div>
      <p className="mt-3 font-semibold text-gray-700">{title}</p>
      {hint && <p className="text-sm text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function SkeletonRows({ cols, rows = 5 }) {
  return Array.from({ length: rows }).map((_, i) => (
    <tr key={i} className="border-t border-gray-50">
      {Array.from({ length: cols }).map((__, c) => (
        <td key={c} className="px-4 py-4"><div className="h-4 rounded-full bg-gray-100 animate-pulse" style={{ width: `${55 + ((i + c) % 4) * 10}%` }} /></td>
      ))}
    </tr>
  ));
}

export default function ContactEnquiriesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState({ key: 'received', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [viewRow, setViewRow] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/contact-enquiries');
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load enquiries. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [search, typeFilter, statusFilter, companyFilter, dateFrom, dateTo, sort, pageSize]);

  const onSort = (key) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));

  const typeOptions = useMemo(
    () => Array.from(new Set([...TYPES, ...rows.map((r) => r.enquiry_type).filter(Boolean)])),
    [rows]
  );

  // Everything except the status filter, so the status tabs can show their own counts
  const baseFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (typeFilter && r.enquiry_type !== typeFilter) return false;
      if (companyFilter === 'with' && !String(r.company_name || '').trim()) return false;
      if (companyFilter === 'without' && String(r.company_name || '').trim()) return false;
      if (dateFrom || dateTo) {
        if (!r.created_at) return false;
        const d = new Date(r.created_at).setHours(0, 0, 0, 0);
        if (dateFrom && d < new Date(dateFrom).setHours(0, 0, 0, 0)) return false;
        if (dateTo && d > new Date(dateTo).setHours(23, 59, 59, 999)) return false;
      }
      if (!q) return true;
      return (
        r.full_name?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        r.mobile?.includes(search.trim()) ||
        r.company_name?.toLowerCase().includes(q) ||
        r.message?.toLowerCase().includes(q)
      );
    });
  }, [rows, search, typeFilter, companyFilter, dateFrom, dateTo]);

  const statusCounts = useMemo(() => {
    const c = { new: 0, contacted: 0, resolved: 0 };
    baseFiltered.forEach((r) => { c[r.status || 'new'] = (c[r.status || 'new'] || 0) + 1; });
    return c;
  }, [baseFiltered]);

  const filtered = useMemo(
    () => (statusFilter ? baseFiltered.filter((r) => (r.status || 'new') === statusFilter) : baseFiltered),
    [baseFiltered, statusFilter]
  );

  const sortValue = (r, key) => {
    switch (key) {
      case 'received': return r.created_at ? new Date(r.created_at).getTime() : null;
      case 'name': return r.full_name || '';
      case 'email': return r.email || '';
      case 'mobile': return r.mobile || '';
      case 'type': return r.enquiry_type || '';
      case 'company': return r.company_name || '';
      case 'message': return r.message || '';
      case 'status': return STATUS_RANK[r.status || 'new'] ?? 9;
      default: return null;
    }
  };

  const sorted = useMemo(
    () => filtered.slice().sort((a, b) => compareSort(sortValue(a, sort.key), sortValue(b, sort.key), sort.dir)),
    [filtered, sort]
  );

  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const cur = Math.min(page, pages);
  const paged = sorted.slice((cur - 1) * pageSize, cur * pageSize);

  const totalNew = rows.filter((r) => (r.status || 'new') === 'new').length;
  const totalContacted = rows.filter((r) => r.status === 'contacted').length;
  const totalResolved = rows.filter((r) => r.status === 'resolved').length;

  const changeStatus = async (row, status) => {
    const prev = rows;
    setRows((p) => p.map((r) => (r.id === row.id ? { ...r, status } : r)));
    setViewRow((v) => (v && v.id === row.id ? { ...v, status } : v));
    try {
      await api.put(`/admin/contact-enquiries/${row.id}/status`, { status });
    } catch (err) {
      setRows(prev);
      alert(err.response?.data?.message || 'Could not update the status. Try again.');
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete enquiry from "${row.full_name}"? This cannot be undone.`)) return;
    setBusyId(row.id);
    try {
      await api.delete(`/admin/contact-enquiries/${row.id}`);
      setRows((p) => p.filter((r) => r.id !== row.id));
      setViewRow(null);
    } catch (err) {
      alert(err.response?.data?.message || 'Could not delete the enquiry. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  const clearFilters = () => {
    setSearch(''); setTypeFilter(''); setStatusFilter(''); setCompanyFilter(''); setDateFrom(''); setDateTo('');
  };

  const chips = [
    search && { k: 'q', text: `Search: ${search}`, clear: () => setSearch('') },
    typeFilter && { k: 't', text: typeFilter, clear: () => setTypeFilter('') },
    statusFilter && { k: 's', text: STATUSES.find((s) => s.value === statusFilter)?.label, clear: () => setStatusFilter('') },
    companyFilter && { k: 'c', text: companyFilter === 'with' ? 'Has company' : 'No company', clear: () => setCompanyFilter('') },
    dateFrom && { k: 'df', text: `From ${dateFrom}`, clear: () => setDateFrom('') },
    dateTo && { k: 'dt', text: `To ${dateTo}`, clear: () => setDateTo('') },
  ].filter(Boolean);

  const activePreset = DATE_PRESETS.find((p) => {
    const r = presetRange(p.days);
    return dateFrom === r.from && dateTo === r.to;
  })?.days;

  const cards = [
    { title: 'All enquiries', value: rows.length, icon: '📨', tone: 'from-[#7C3AED] to-[#A78BFA]', status: '' },
    { title: 'New', value: totalNew, icon: '🆕', tone: 'from-[#F59E0B] to-[#FBBF24]', status: 'new' },
    { title: 'Contacted', value: totalContacted, icon: '📞', tone: 'from-[#0EA5E9] to-[#38BDF8]', status: 'contacted' },
    { title: 'Resolved', value: totalResolved, icon: '✅', tone: 'from-[#10B981] to-[#34D399]', status: 'resolved' },
  ];

  const tabs = [
    { value: '', label: 'All', count: baseFiltered.length },
    ...STATUSES.map((s) => ({ value: s.value, label: s.label, count: statusCounts[s.value] || 0 })),
  ];

  const cols = [
    ['Received', 'received'], ['Name', 'name'], ['Email', 'email'], ['Mobile', 'mobile'],
    ['Type', 'type'], ['Company', 'company'], ['Message', 'message'], ['Status', 'status'],
  ];

  return (
    <div className="space-y-5">
      {/* Summary (click a card to filter by status) */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {cards.map((c) => (
          <button
            key={c.title}
            type="button"
            onClick={() => setStatusFilter(c.status)}
            className={`text-left bg-white rounded-2xl shadow-md shadow-purple-100/60 border p-5 flex items-center gap-4 transition hover:-translate-y-0.5 hover:shadow-lg ${
              statusFilter === c.status ? 'border-purple-300 ring-2 ring-purple-200' : 'border-purple-50'
            }`}
          >
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.tone} text-white flex items-center justify-center text-xl shadow-sm shrink-0`}>{c.icon}</div>
            <div>
              <p className="text-2xl font-extrabold text-[#0D1B3D] leading-tight">{c.value}</p>
              <p className="text-sm font-semibold text-gray-600">{c.title}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[#0D1B3D]">Filter enquiries</h3>
          {chips.length > 0 && (
            <button type="button" onClick={clearFilters}
              className="text-xs font-semibold px-3 py-1.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 transition">
              ✕ Clear all
            </button>
          )}
        </div>

        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm pointer-events-none">🔍</span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, mobile, company or message"
            className={`${FIELD} pl-10`}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <SelectBox caption="Enquiry type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">All enquiry types</option>
            {typeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
          </SelectBox>
          <SelectBox caption="Company" value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}>
            <option value="">All</option>
            <option value="with">Has a company name</option>
            <option value="without">No company name</option>
          </SelectBox>
          <div className="flex flex-wrap gap-2 items-end pb-0.5">
            {DATE_PRESETS.map((p) => (
              <button key={p.days} type="button"
                onClick={() => { const r = presetRange(p.days); setDateFrom(r.from); setDateTo(r.to); }}
                className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition ${
                  activePreset === p.days
                    ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm'
                    : 'bg-white text-purple-700 border-purple-200 hover:border-purple-400'
                }`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className={CAPTION}>Received from</label>
            <input type="date" className={`${FIELD} w-44`} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className={CAPTION}>Received to</label>
            <input type="date" className={`${FIELD} w-44`} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </div>

        {chips.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {chips.map((c) => (
              <span key={c.k} className="inline-flex items-center gap-1.5 text-xs font-medium pl-3 pr-1.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
                {c.text}
                <button type="button" onClick={c.clear} className="h-4 w-4 rounded-full hover:bg-purple-200 leading-none" aria-label="Remove filter">×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {error && !loading && (
        <div className="flex items-center justify-between gap-3 bg-red-50 border border-red-100 text-red-700 rounded-xl px-4 py-3 text-sm">
          <span>{error}</span>
          <button type="button" onClick={load} className="font-semibold underline">Retry</button>
        </div>
      )}

      {/* Status tabs */}
      <div className="flex gap-1 border-b border-gray-100 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.label}
            type="button"
            onClick={() => setStatusFilter(t.value)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition inline-flex items-center gap-2 whitespace-nowrap ${
              statusFilter === t.value ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            {t.label}
            <span className={`text-[11px] font-bold h-5 min-w-[1.25rem] px-1.5 rounded-full flex items-center justify-center ${
              statusFilter === t.value ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'
            }`}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 text-xs text-gray-400">
          Showing <span className="font-semibold text-purple-600">{sorted.length}</span> of {rows.length} enquiries. Click a column heading to sort.
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1250px]">
            <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
              <tr>
                {cols.map(([h, k]) => <SortTh key={k} label={h} sortKey={k} sort={sort} onSort={onSort} />)}
                <th className="text-left px-4 py-3 font-semibold sticky right-0 z-20 bg-gray-50 border-l border-gray-100 shadow-[-6px_0_8px_-4px_rgba(0,0,0,0.08)]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonRows cols={9} />
              ) : paged.length === 0 ? (
                <tr><td colSpan={9}>
                  <EmptyState
                    icon={rows.length === 0 ? '📭' : '🔎'}
                    title={rows.length === 0 ? 'No enquiries yet' : 'No enquiries match your filters'}
                    hint={rows.length === 0 ? 'New messages from the contact form will appear here.' : 'Clear a filter or widen the date range.'}
                  />
                </td></tr>
              ) : (
                paged.map((r) => {
                  const status = r.status || 'new';
                  const wa = waLink(r.mobile);
                  return (
                    <tr key={r.id} className="group border-t border-gray-50 hover:bg-purple-50/60 transition-colors align-top">
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        <span className="inline-flex items-center gap-2">
                          {status === 'new' && <span className="w-2 h-2 rounded-full bg-amber-400" title="New" />}
                          {fmtDateTime(r.created_at)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button type="button" onClick={() => setViewRow(r)} className="flex items-center gap-3 text-left">
                          <Avatar name={r.full_name} />
                          <span className="font-semibold text-[#7C3AED] hover:underline">{r.full_name}</span>
                        </button>
                      </td>
                      <td className="px-4 py-3 max-w-[200px] truncate" title={r.email}>{r.email}</td>
                      <td className="px-4 py-3 tabular-nums whitespace-nowrap">{r.mobile}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${typeTone(r.enquiry_type)}`}>{r.enquiry_type}</span>
                      </td>
                      <td className="px-4 py-3">{r.company_name || <span className="text-gray-300">—</span>}</td>
                      <td className="px-4 py-3 max-w-[240px]">
                        <p className="text-gray-600 leading-snug break-words" title={r.message}
                          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {r.message || '—'}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={status}
                          onChange={(e) => changeStatus(r, e.target.value)}
                          className={`text-xs font-semibold rounded-full px-3 py-1.5 border-none cursor-pointer ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-500'}`}
                        >
                          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 sticky right-0 z-10 bg-white group-hover:bg-purple-50 border-l border-gray-100 shadow-[-6px_0_8px_-4px_rgba(0,0,0,0.08)]">
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={() => setViewRow(r)} className={`${ACT} bg-purple-50 text-[#7C3AED] border-purple-100 hover:bg-purple-100`}>👁 View</button>
                          {r.mobile && <a href={`tel:${r.mobile}`} className={`${ACT} bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100`}>📞 Call</a>}
                          {wa && <a href={wa} target="_blank" rel="noreferrer" className={`${ACT} bg-green-50 text-green-600 border-green-100 hover:bg-green-100`}>💬 WhatsApp</a>}
                          <button type="button" onClick={() => remove(r)} disabled={busyId === r.id} className={`${ACT} bg-red-50 text-red-500 border-red-100 hover:bg-red-100`}>🗑 Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between flex-wrap gap-3 text-sm text-gray-500">
          <div className="flex items-center gap-3">
            <span>Page {cur} of {pages}</span>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white">
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
            </select>
          </div>
          <Pager page={cur} lastPage={pages} onPage={setPage} />
        </div>
      </div>

      {/* Detail popup */}
      {viewRow && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4" onClick={() => setViewRow(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[88vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-purple-700 to-pink-600 px-6 py-5 flex items-start justify-between shrink-0">
              <div>
                <h3 className="text-lg font-bold text-white">{viewRow.full_name}</h3>
                <p className="text-purple-100 text-xs mt-0.5">{viewRow.enquiry_type} · received {fmtDateTime(viewRow.created_at)}</p>
              </div>
              <button onClick={() => setViewRow(null)} className="text-purple-100 hover:text-white text-2xl leading-none" aria-label="Close">×</button>
            </div>

            <div className="px-6 py-5 overflow-y-auto space-y-4 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-xl bg-gray-50 p-3">
                  <p className="text-xs text-gray-400">Email</p>
                  <a className="text-[#7C3AED] hover:underline break-all" href={`mailto:${viewRow.email}`}>{viewRow.email}</a>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <p className="text-xs text-gray-400">Mobile</p>
                  <p className="text-gray-700 tabular-nums">{viewRow.mobile || '—'}</p>
                </div>
                {viewRow.company_name && (
                  <div className="rounded-xl bg-gray-50 p-3 sm:col-span-2">
                    <p className="text-xs text-gray-400">Company</p>
                    <p className="text-gray-700">{viewRow.company_name}</p>
                  </div>
                )}
              </div>

              <div>
                <p className="text-xs text-gray-400 mb-1">Message</p>
                <p className="whitespace-pre-wrap break-words text-gray-700 bg-purple-50/50 border border-purple-100 rounded-xl p-3">{viewRow.message}</p>
              </div>

              <p className="text-xs text-gray-400">Consent given: {fmtDateTime(viewRow.consent_at)}</p>

              <div className="flex flex-wrap gap-2">
                {viewRow.mobile && <a href={`tel:${viewRow.mobile}`} className={`${ACT} bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100`}>📞 Call</a>}
                {waLink(viewRow.mobile) && <a href={waLink(viewRow.mobile)} target="_blank" rel="noreferrer" className={`${ACT} bg-green-50 text-green-600 border-green-100 hover:bg-green-100`}>💬 WhatsApp</a>}
                {viewRow.email && <a href={`mailto:${viewRow.email}`} className={`${ACT} bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-100`}>✉️ Email</a>}
              </div>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 gap-3 flex-wrap shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500">Status</span>
                <select
                  value={viewRow.status || 'new'}
                  onChange={(e) => changeStatus(viewRow, e.target.value)}
                  className={`text-xs font-semibold rounded-full px-3 py-1.5 border-none cursor-pointer ${STATUS_STYLES[viewRow.status || 'new']}`}
                >
                  {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <button onClick={() => remove(viewRow)} disabled={busyId === viewRow.id} className="text-sm font-semibold text-red-500 hover:underline disabled:opacity-40">
                Delete enquiry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}